import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { TableSkeleton } from '../components/Skeleton';
import { formatBidNumber } from '../utils/bidNumber';

const STATUS_CLASS = {
  'Bidding': 'status-bidding',
  'Pending': 'status-pending',
  'Budget': 'status-budget',
  'On Hold': 'status-on-hold',
  'Pipeline': 'status-pipeline',
  'Awarded': 'status-awarded',
  'Not Awarded': 'status-not-awarded',
};

const STATUS_DISPLAY = { 'Pipeline': 'Opportunities' };
function displayStatus(s) { return STATUS_DISPLAY[s] || s; }

function formatCurrency(val) {
  if (!val || val === 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

function formatDate(val) {
  if (!val) return '-';
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export default function ClientPage() {
  const { name } = useParams();
  const clientName = decodeURIComponent(name);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getProjects({ client: clientName })
      .then(setProjects)
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [clientName]);

  const totalBids = projects.length;
  const winCount = projects.filter(p => p.StatusName === 'Awarded').length;
  const lossCount = projects.filter(p => p.StatusName === 'Not Awarded').length;
  const winRate = totalBids > 0 ? ((winCount / totalBids) * 100).toFixed(1) : '0.0';
  const totalValue = projects.reduce((sum, p) => sum + (p.ProjectCost || 0), 0);

  return (
    <>
      <div className="top-bar">
        <div>
          <nav style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
            <Link to="/" style={{ color: '#6b7280', textDecoration: 'none' }}>Home</Link>
            <span style={{ margin: '0 6px' }}>&gt;</span>
            <span style={{ color: '#6b7280' }}>Clients</span>
            <span style={{ margin: '0 6px' }}>&gt;</span>
            <span style={{ color: '#111827', fontWeight: 500 }}>{clientName}</span>
          </nav>
          <h2>{clientName}</h2>
        </div>
      </div>

      <div className="page-content">
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-label">Total Bids</div>
            <div className="stat-value">{totalBids}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Wins</div>
            <div className="stat-value">{winCount}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Losses</div>
            <div className="stat-value">{lossCount}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Win Rate</div>
            <div className="stat-value">{winRate}%</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Value</div>
            <div className="stat-value">{formatCurrency(totalValue)}</div>
          </div>
        </div>

        <div className="card">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Bid #</th>
                  <th>Project Name</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>State</th>
                  <th>Estimator</th>
                  <th>Estimating Coordinator</th>
                  <th>ITB Received</th>
                  <th>Deadline</th>
                  <th style={{ textAlign: 'right' }}>Project Cost</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : projects.length === 0 ? (
                  <tr><td colSpan="10">
                    <div className="empty-state">
                      <h3>No projects found for this client</h3>
                    </div>
                  </td></tr>
                ) : projects.map(p => (
                  <tr key={p.ProjectId} className="clickable-row" onClick={() => navigate(`/projects/${p.ProjectId}`)}>
                    <td className="bid-number">{formatBidNumber(p.BidNumber, p.RevisionNumber)}</td>
                    <td className="project-name">{p.ProjectName}</td>
                    <td>
                      <span className={`status-badge ${STATUS_CLASS[p.StatusName] || ''}`}>{displayStatus(p.StatusName)}</span>
                    </td>
                    <td>{p.ProjectType || '-'}</td>
                    <td>{p.State || '-'}</td>
                    <td>{p.Estimator || '-'}</td>
                    <td>{p.BidCoordinator || '-'}</td>
                    <td>{formatDate(p.ITBReceived)}</td>
                    <td>{formatDate(p.GCOwnerDeadline)}</td>
                    <td className="currency">{formatCurrency(p.ProjectCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
